import _ from './notification-badges.module.scss'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {IconButton} from '../../../../components/icon-button'
import {useSharedStore} from '../../../../state/shared-store-context'
import {$, $$$} from '../../../../shared/class-name'
import {formatCount} from '../../../../shared/format-count'
import {getSessionUser} from '../../../../shared/session'
import {getProfilePath, isProfilePath} from '../../shared/profile-url'
import {useIs1024} from '../../../../hooks/use-is-1024'
import {getNotificationBadgeRouteState} from './notification-badge-route-state'
import {
    slots,
    notifTypes,
    menuProfileKeys,
    menuNetworkKeys,
    notificationsKeys,
    networkKeys,
    toolbarKeys,
} from '../../notifications-config'

const ANIM_MS = 270

export const NotificationBadges = ({notifSummary, containerName, fadingOut: allFadingOut}) => {
    const {pathname} = useLocation()
    const navigate = useNavigate()
    const is1024 = useIs1024()
    const {sharedState} = useSharedStore()
    const notificationSettings = sharedState.notificationSettings
    const profileUserId = sharedState.routeParams.profileUserId || sharedState.routeParams.userId || null
    const sessionUser = getSessionUser()
    const isProfile = isProfilePath(pathname)
    const {notificationsHidden, notificationMenuHidden, networkHidden} = getNotificationBadgeRouteState({
        pathname, profileUserId, sessionUser, containerName,
    })
    const supportedNotifications = ({
        'notifications': notificationsKeys,
        'network': networkKeys,
    })[containerName] || toolbarKeys

    const positioned = useMemo(() => {
        const newActive = notifSummary
            ? notifTypes.filter(t => notifSummary[t.valueKey] > 0 && notificationSettings?.[t.key] !== false
                && supportedNotifications.includes(t.key)
                && !notificationMenuHidden
                && !(notificationsHidden && !menuNetworkKeys.includes(t.key) && notificationsKeys.includes(t.key))
                && !(networkHidden && networkKeys.includes(t.key)))
            : []
        return assignPositions(newActive, containerName, is1024, isProfile)
            .map(t => ({...t, value: notifSummary?.[t.valueKey]}))
    }, [notifSummary, notificationSettings, supportedNotifications, notificationMenuHidden, notificationsHidden,
        networkHidden, containerName, is1024, isProfile])

    const [displayed, setDisplayed] = useState([])
    const timersRef = useRef({})
    const prevAllFadingOutRef = useRef(false)
    const positionedDep = positioned.map(p => `${p.key}:${p.value}:${slotSignature(p.slot)}`).join(',')

    useEffect(() => {
        const wasAllFadingOut = prevAllFadingOutRef.current
        prevAllFadingOutRef.current = allFadingOut

        if (wasAllFadingOut && !allFadingOut) {
            Object.values(timersRef.current).forEach(clearTimeout)
            timersRef.current = {}
            setDisplayed(positioned)
            return
        }
        if (allFadingOut) return

        const activeKeys = new Set(positioned.map(p => p.key))
        setDisplayed(prev => {
            const kept = prev.map(item => {
                if (activeKeys.has(item.key)) {
                    const p = positioned.find(p => p.key === item.key)
                    return {...item, fadingOut: false, value: p.value, slot: p.slot}
                }
                if (item.fadingOut) return item
                if (!timersRef.current[item.key]) {
                    timersRef.current[item.key] = setTimeout(() => {
                        setDisplayed(d => d.filter(x => x.key !== item.key))
                        delete timersRef.current[item.key]
                    }, ANIM_MS)
                }
                return {...item, fadingOut: true}
            })
            const keptKeys = new Set(kept.map(k => k.key))
            const added = positioned.filter(p => !keptKeys.has(p.key))
            return [...kept, ...added]
        })
    }, [positionedDep, positioned, allFadingOut])

    useEffect(() => () => { Object.values(timersRef.current).forEach(clearTimeout) }, [])

    if (!displayed.length || (!positioned.length && !allFadingOut)) return null

    return <>{displayed.map(({key, id, value, color, icon, slot, url, fadingOut}) => {
        const {center, slotKey, ...pos} = slot
        const posStyle = Object.fromEntries(Object.entries(pos).map(([k, v]) => [k, `${v}px`]))
        const isFading = fadingOut || allFadingOut
        return <div key={id || key} className={$$$(_.badge, $(_.fadingOut, isFading))} style={posStyle}>
            <div className={$$$(
                _.badgeTransform,
                $(_.centerLeft, center === 'left'),
                $(_.centerRight, center === 'right'),
            )}>
                <IconButton className={$$$(_.button, $(_[key], key))} color={color} size="size24" autoWidth
                    onClick={() => !isFading
                        && navigate(url || `${getProfilePath(getSessionUser())}/notifications`)}>
                    {'+'}{formatCount(value)}&nbsp;{icon}
                </IconButton>
            </div>
        </div>
    })}</>
}

function assignPositions(active, containerName, is1024, isProfile) {
    if (containerName === 'notifications') {
        const currentSlots = getSlots(slots.notifications, is1024, isProfile)
        return active
            .filter(t => notificationsKeys.includes(t.key))
            .sort((a, b) => notificationsKeys.indexOf(a.key) - notificationsKeys.indexOf(b.key))
            .map((t, i) => ({...t, slot: getSlot(currentSlots, t, i)}))
    }
    if (containerName === 'network') {
        const currentSlots = getSlots(slots.network, is1024, isProfile)
        return active
            .filter(t => networkKeys.includes(t.key))
            .sort((a, b) => networkKeys.indexOf(a.key) - networkKeys.indexOf(b.key))
            .map((t, i) => ({...t, slot: getSlot(currentSlots, t, i)}))
    }
    const profileSlots = getSlots(slots.toolbars.profile, is1024, isProfile)
    const networkSlots = getSlots(slots.toolbars.network, is1024, isProfile)
    const networkAlternativeSlots = getSlots(slots.toolbars.network, is1024, isProfile, true)
    const profileItems = active
        .filter(t => menuProfileKeys.includes(t.key))
        .sort((a, b) => menuProfileKeys.indexOf(a.key) - menuProfileKeys.indexOf(b.key))
        .map((t, i) => ({...t, slot: getSlot(profileSlots, t, i)}))
    const networkItems = active.filter(t => menuNetworkKeys.includes(t.key))
    const currentNetworkSlots = networkItems.length === 1 ? networkAlternativeSlots : networkSlots
    const positionedNetworkItems = networkItems
        .sort((a, b) => menuNetworkKeys.indexOf(a.key) - menuNetworkKeys.indexOf(b.key))
        .map((t, i) => ({...t, slot: getSlot(currentNetworkSlots, t, i)}))
    return [...profileItems, ...positionedNetworkItems]
}

function getSlots(availableSlots, is1024, isProfile, single = false) {
    if (!is1024) return availableSlots[single ? 'mobileSingle' : 'mobile'] || availableSlots.mobile
    if (isProfile) return availableSlots[single ? 'desktopProfilePathSingle' : 'desktopProfilePath']
        || availableSlots.desktopProfilePath || availableSlots.desktop
    return availableSlots[single ? 'desktopSingle' : 'desktop'] || availableSlots.desktop
}

const getSlot = (availableSlots, item, index) =>
    availableSlots.find(slot => slot.slotKey === item.key) || availableSlots[index]

const slotSignature = (slot = {}) => Object.entries(slot)
    .map(([key, value]) => `${key}:${value}`)
    .join('|')
